import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Play, Pause, Archive, Trash2, Edit2, Users, Mail,
  ChevronRight, MoreVertical,
  Zap, List, Loader2, UserPlus, X, Building, Send
} from 'lucide-react'
import toast from 'react-hot-toast'
import { getMyOrganization, setAutoProjectRelaunchSequence } from '../../services/api.js'
import { useAuthStore } from '../../stores/authStore.js'
import {
  getEmailSequences,
  createEmailSequence,
  updateEmailSequence,
  deleteEmailSequence,
  getSequenceEnrollments
} from '../../services/api'
import SequenceWorkflowBuilder from './SequenceWorkflowBuilder'
import EnrollProspectsModal from './EnrollProspectsModal'
import SequenceActivationModal from './SequenceActivationModal'
import SequenceRecipientsTab from './SequenceRecipientsTab'

// CSS pour l'animation de spin
const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`
if (typeof document !== 'undefined' && !document.getElementById('spin-keyframes')) {
  const style = document.createElement('style')
  style.id = 'spin-keyframes'
  style.textContent = spinKeyframes
  document.head.appendChild(style)
}

const STATUS_LABELS = {
  draft: { label: 'Brouillon', color: 'var(--color-text-secondary)', bg: 'rgba(136,136,136,0.1)' },
  active: { label: 'Active', color: 'rgb(var(--rgb-brand))', bg: 'rgb(var(--rgb-brand) / 0.1)' },
  paused: { label: 'En pause', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  archived: { label: 'Archivée', color: '#ef4444', bg: 'rgba(239,68,68,0.1)' }
}

export default function EmailSequences() {
  const queryClient = useQueryClient()
  const authUser = useAuthStore((state) => state.user)
  // Séquence de relance des projets auto : choisie par le propriétaire de l'organisation (ou un admin).
  const { data: organizationData } = useQuery({ queryKey: ['myOrganization'], queryFn: getMyOrganization, staleTime: 60_000 })
  const organization = organizationData?.organization
  const relaunchSequenceId = organization?.autoProjectRelaunchSequenceId ?? null
  const canChooseRelaunch = Boolean(organization) && (organization.myRole === 'owner' || authUser?.roles?.includes('ROLE_ADMIN'))
  const relaunchMutation = useMutation({
    mutationFn: setAutoProjectRelaunchSequence,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['myOrganization'] })
      toast.success(data.sequenceId ? `« ${data.sequenceName} » relancera les projets auto` : 'Relances des projets auto désactivées')
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Choix impossible'),
  })
  const [editingSequence, setEditingSequence] = useState(null)
  const [selectedSequence, setSelectedSequence] = useState(null)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [viewMode, setViewMode] = useState('list') // 'list' | 'builder' | 'loading'
  const [showEnrollModal, setShowEnrollModal] = useState(false)
  const [showEnrollments, setShowEnrollments] = useState(false)
  const [showActivationModal, setShowActivationModal] = useState(false)
  const [sequenceToActivate, setSequenceToActivate] = useState(null)
  const [sequenceTab, setSequenceTab] = useState('workflow') // 'workflow' | 'prospects' | 'recipients'

  const handleCreateNewSequence = async () => {
    setViewMode('loading')
    // Court délai pour donner un feedback visuel (600-900ms)
    await new Promise(resolve => setTimeout(resolve, 600 + Math.random() * 300))
    setViewMode('builder')
  }

  // Fetch sequences
  const { data: sequencesData, isLoading } = useQuery({
    queryKey: ['emailSequences'],
    queryFn: getEmailSequences
  })

  const sequences = sequencesData?.member || sequencesData?.['hydra:member'] || sequencesData || []

  // Fetch enrollment counts for all sequences
  const { data: enrollmentCounts } = useQuery({
    queryKey: ['sequenceEnrollmentCounts', sequences.map(s => s.id)],
    queryFn: async () => {
      const counts = {}
      await Promise.all(sequences.map(async (sequence) => {
        try {
          // Fetch with limit=1 just to get the total count
          const enrollmentsData = await getSequenceEnrollments(sequence.id, { limit: 1 })
          // Use total from API response, not array length (which is paginated)
          counts[sequence.id] = enrollmentsData?.total ?? enrollmentsData?.['hydra:totalItems'] ?? 0
        } catch (error) {
          console.error(`Error fetching enrollments for sequence ${sequence.id}:`, error)
          counts[sequence.id] = 0
        }
      }))
      return counts
    },
    enabled: sequences.length > 0
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createEmailSequence,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailSequences'] })
      setViewMode('list')
      toast.success('Séquence créée avec succès')
    },
    onError: (err) => toast.error(err.message || 'Erreur création')
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateEmailSequence(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailSequences'] })
      queryClient.invalidateQueries({ queryKey: ['sequenceEnrollmentCounts'] })
      toast.success('Séquence mise à jour')
    },
    onError: (err) => toast.error(err.message || 'Erreur mise à jour')
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteEmailSequence,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailSequences'] })
      toast.success('Séquence supprimée')
    },
    onError: (err) => toast.error(err.message || 'Erreur suppression')
  })

  const handleStatusChange = (sequence, newStatus) => {
    // Si activation, ouvrir le modal de confirmation
    if (newStatus === 'active' && sequence.status !== 'active') {
      setSequenceToActivate(sequence)
      setShowActivationModal(true)
      setActiveDropdown(null)
      return
    }
    
    // Autres changements de statut directs
    updateMutation.mutate({ id: sequence.id, data: { status: newStatus } })
    setActiveDropdown(null)
  }

  const confirmActivation = async () => {
    if (!sequenceToActivate) return
    await updateMutation.mutateAsync({ 
      id: sequenceToActivate.id, 
      data: { status: 'active' } 
    })
    setShowActivationModal(false)
    setSequenceToActivate(null)
  }

  const handleDelete = (sequence) => {
    if (confirm(`Supprimer la séquence "${sequence.name}" ?`)) {
      deleteMutation.mutate(sequence.id)
    }
    setActiveDropdown(null)
  }

  // Si on édite une séquence, afficher avec sous-onglets
  if (selectedSequence) {
    const SEQUENCE_TABS = [
      { id: 'workflow', label: 'Workflow', icon: Zap },
      { id: 'prospects', label: 'Prospects', icon: Users },
      { id: 'recipients', label: 'Destinataires', icon: Send }
    ]

    return (
      <div className="sequences-container">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
              {selectedSequence.name}
            </h2>
            <span style={{
              padding: '0.25rem 0.75rem',
              background: STATUS_LABELS[selectedSequence.status]?.bg || 'rgba(136,136,136,0.1)',
              color: STATUS_LABELS[selectedSequence.status]?.color || 'var(--color-text-secondary)',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              {STATUS_LABELS[selectedSequence.status]?.label || 'Brouillon'}
            </span>
          </div>
          <button
            onClick={() => {
              setSelectedSequence(null)
              setSequenceTab('workflow')
            }}
            style={{
              background: 'rgb(var(--rgb-ink) / 0.1)',
              color: 'var(--color-text)',
              border: '1px solid rgb(var(--rgb-ink) / 0.2)',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem'
            }}
          >
            <List size={16} />
            Retour
          </button>
        </div>

        {/* Sub-tabs navigation */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          borderBottom: '1px solid rgb(var(--rgb-ink) / 0.1)',
          paddingBottom: '0.5rem'
        }}>
          {SEQUENCE_TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = sequenceTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setSequenceTab(tab.id)}
                style={{
                  background: isActive ? 'rgb(var(--rgb-brand) / 0.1)' : 'transparent',
                  color: isActive ? 'rgb(var(--rgb-brand))' : 'var(--color-text-secondary)',
                  border: isActive ? '1px solid rgb(var(--rgb-brand) / 0.3)' : '1px solid transparent',
                  padding: '0.6rem 1rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.9rem',
                  fontWeight: isActive ? 600 : 400,
                  transition: 'all 0.2s'
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Tab content */}
        {sequenceTab === 'workflow' && (
          <SequenceWorkflowBuilder
            sequence={selectedSequence}
            onSave={() => {
              queryClient.invalidateQueries({ queryKey: ['emailSequences'] })
            }}
          />
        )}

        {sequenceTab === 'prospects' && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                Gerez les prospects inscrits a cette sequence
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => setShowEnrollments(true)}
                  style={{
                    background: 'rgba(59, 130, 246, 0.15)',
                    color: '#3b82f6',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  <Users size={16} />
                  Voir les inscrits
                </button>
                <button
                  onClick={() => setShowEnrollModal(true)}
                  style={{
                    background: 'rgb(var(--rgb-brand))',
                    color: '#fff',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}
                >
                  <UserPlus size={16} />
                  Ajouter des prospects
                </button>
              </div>
            </div>
            {/* Inline enrollments panel for prospects */}
            <ProspectsEnrollmentsList sequenceId={selectedSequence.id} />
          </div>
        )}

        {sequenceTab === 'recipients' && (
          <SequenceRecipientsTab sequence={selectedSequence} />
        )}

        {/* Modal d'enrollment prospects */}
        {showEnrollModal && (
          <EnrollProspectsModal
            sequence={selectedSequence}
            onClose={() => setShowEnrollModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments', selectedSequence.id] })
            }}
          />
        )}

        {/* Panel des inscrits (modal) */}
        {showEnrollments && (
          <EnrollmentsPanel
            sequenceId={selectedSequence.id}
            onClose={() => setShowEnrollments(false)}
          />
        )}
      </div>
    )
  }

  // Écran de chargement lors de la création
  if (viewMode === 'loading') {
    return (
      <div className="sequences-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '1.5rem'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: 'rgb(var(--rgb-brand) / 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Loader2 
            size={24} 
            style={{ 
              color: 'rgb(var(--rgb-brand))',
              animation: 'spin 1s linear infinite'
            }} 
          />
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--color-text)', fontSize: '1rem', fontWeight: 500, marginBottom: '0.5rem' }}>
            Création du workflow...
          </p>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
            Préparation de l'éditeur
          </p>
        </div>
      </div>
    )
  }

  // Vue Workflow Builder
  if (viewMode === 'builder') {
    return (
      <div className="sequences-container">
        {/* Header avec retour */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
              Workflow Builder
            </h2>
            <span style={{ 
              padding: '0.25rem 0.75rem', 
              background: 'rgb(var(--rgb-brand) / 0.1)', 
              color: 'rgb(var(--rgb-brand))',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              BETA
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setViewMode('list')}
              style={{
                background: 'transparent',
                border: '1px solid rgb(var(--rgb-ink) / 0.2)',
                color: 'var(--color-text)',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem'
              }}
            >
              <List size={16} />
              Vue Liste
            </button>
          </div>
        </div>

        <SequenceWorkflowBuilder 
          onSave={() => {
            queryClient.invalidateQueries({ queryKey: ['emailSequences'] })
          }}
        />
      </div>
    )
  }

  return (
    <div className="sequences-container">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.25rem' }}>
            Séquences Email
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
            Automatisez vos suivis avec des séquences multi-étapes
          </p>
        </div>
        <button
          onClick={handleCreateNewSequence}
          style={{
            background: 'rgb(var(--rgb-brand))',
            color: '#fff',
            border: 'none',
            padding: '0.75rem 1.25rem',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Plus size={18} />
          Nouvelle séquence
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
          Chargement...
        </div>
      )}

      {/* Empty state */}
      {!isLoading && sequences.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: 'rgb(var(--rgb-ink) / 0.02)',
          borderRadius: '12px',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)'
        }}>
          <Zap size={48} style={{ color: 'rgb(var(--rgb-brand))', marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>
            Aucune séquence
          </h3>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
            Créez votre première séquence pour automatiser vos emails de suivi
          </p>
          <button
            onClick={handleCreateNewSequence}
            style={{
              background: 'rgb(var(--rgb-brand))',
              color: '#fff',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <Plus size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Créer une séquence
          </button>
        </div>
      )}

      {/* Sequences list */}
      {!isLoading && sequences.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {sequences.map((sequence) => {
            const status = STATUS_LABELS[sequence.status] || STATUS_LABELS.draft
            // Compter les vraies étapes email dans le workflow
            const workflowNodes = sequence.workflowData?.nodes || []
            const emailSteps = workflowNodes.filter(node => node.type === 'email')
            const stepsCount = emailSteps.length
            const enrolledCount = enrollmentCounts?.[sequence.id] || 0

            return (
              <div
                key={sequence.id}
                style={{
                  background: 'rgb(var(--rgb-ink) / 0.02)',
                  border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => setSelectedSequence(sequence)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgb(var(--rgb-brand) / 0.3)'
                  e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.04)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgb(var(--rgb-ink) / 0.1)'
                  e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.02)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  {/* Left side */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>
                        {sequence.name}
                      </h3>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: status.color,
                        background: status.bg
                      }}>
                        {status.label}
                      </span>
                    </div>

                    {sequence.description && (
                      <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                        {sequence.description}
                      </p>
                    )}

                    {/* Stats */}
                    <div style={{ display: 'flex', gap: '1.5rem', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Mail size={14} />
                        {stepsCount} étape{stepsCount > 1 ? 's' : ''}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Users size={14} />
                        {enrolledCount} inscrit{enrolledCount > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Right side - Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                       onClick={(e) => e.stopPropagation()}>
                    {relaunchSequenceId === sequence.id && (
                      <span title="L'agent inscrit chaque nouveau projet auto dans cette séquence" style={{ padding: '0.35rem 0.7rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, color: 'rgb(var(--rgb-brand))', background: 'rgb(var(--rgb-brand) / 0.12)', border: '1px solid rgb(var(--rgb-brand) / 0.35)', whiteSpace: 'nowrap' }}>
                        Relance des projets auto
                      </span>
                    )}
                    {canChooseRelaunch && sequence.status === 'active' && relaunchSequenceId !== sequence.id && (
                      <button
                        type="button"
                        disabled={relaunchMutation.isPending}
                        onClick={() => relaunchMutation.mutate(sequence.id)}
                        title="L'agent des projets auto inscrira chaque projet dans cette séquence après sa réponse"
                        style={{ padding: '0.4rem 0.75rem', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', color: 'var(--color-text-secondary)', background: 'rgb(var(--rgb-ink) / 0.04)', border: '1px solid rgb(var(--rgb-ink) / 0.12)', whiteSpace: 'nowrap' }}
                      >
                        Relancer les projets auto
                      </button>
                    )}
                    {canChooseRelaunch && relaunchSequenceId === sequence.id && (
                      <button
                        type="button"
                        disabled={relaunchMutation.isPending}
                        onClick={() => relaunchMutation.mutate(null)}
                        title="Ne plus relancer les projets auto avec cette séquence"
                        style={{ padding: '0.4rem 0.6rem', borderRadius: '8px', fontSize: '0.75rem', cursor: 'pointer', color: 'var(--color-text-muted)', background: 'transparent', border: '1px solid rgb(var(--rgb-ink) / 0.1)' }}
                      >
                        Retirer
                      </button>
                    )}
                    {sequence.status === 'draft' && (
                      <button
                        onClick={() => handleStatusChange(sequence, 'active')}
                        style={{
                          background: 'rgb(var(--rgb-brand) / 0.1)',
                          color: 'rgb(var(--rgb-brand))',
                          border: '1px solid rgb(var(--rgb-brand) / 0.3)',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        <Play size={14} />
                        Activer
                      </button>
                    )}
                    {sequence.status === 'active' && (
                      <button
                        onClick={() => handleStatusChange(sequence, 'paused')}
                        style={{
                          background: 'rgba(245,158,11,0.1)',
                          color: '#f59e0b',
                          border: '1px solid rgba(245,158,11,0.3)',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        <Pause size={14} />
                        Pause
                      </button>
                    )}
                    {sequence.status === 'paused' && (
                      <button
                        onClick={() => handleStatusChange(sequence, 'active')}
                        style={{
                          background: 'rgb(var(--rgb-brand) / 0.1)',
                          color: 'rgb(var(--rgb-brand))',
                          border: '1px solid rgb(var(--rgb-brand) / 0.3)',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        <Play size={14} />
                        Reprendre
                      </button>
                    )}

                    {/* Dropdown menu */}
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === sequence.id ? null : sequence.id)}
                        style={{
                          background: 'transparent',
                          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color: 'var(--color-text-secondary)'
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>

                      {activeDropdown === sequence.id && (
                        <div style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          marginTop: '0.25rem',
                          background: 'rgb(var(--rgb-surface-hover))',
                          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                          borderRadius: '8px',
                          padding: '0.5rem',
                          minWidth: '160px',
                          zIndex: 100
                        }}>
                          <button
                            onClick={() => {
                              setEditingSequence(sequence)
                              setActiveDropdown(null)
                            }}
                            style={{
                              width: '100%',
                              background: 'transparent',
                              border: 'none',
                              padding: '0.5rem 0.75rem',
                              color: 'var(--color-text)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.9rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <Edit2 size={14} /> Modifier
                          </button>
                          {sequence.status !== 'archived' && (
                            <button
                              onClick={() => handleStatusChange(sequence, 'archived')}
                              style={{
                                width: '100%',
                                background: 'transparent',
                                border: 'none',
                                padding: '0.5rem 0.75rem',
                                color: '#f59e0b',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.9rem'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.05)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              <Archive size={14} /> Archiver
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(sequence)}
                            style={{
                              width: '100%',
                              background: 'transparent',
                              border: 'none',
                              padding: '0.5rem 0.75rem',
                              color: '#ef4444',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.9rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <Trash2 size={14} /> Supprimer
                          </button>
                        </div>
                      )}
                    </div>

                    <ChevronRight size={20} style={{ color: 'var(--color-text-secondary)' }} />
                  </div>
                </div>

              </div>
            )
          })}
        </div>
      )}

      {/* Edit Modal */}
      {editingSequence && (
        <EditSequenceModal
          sequence={editingSequence}
          onClose={() => setEditingSequence(null)}
          onSave={(data) => {
            updateMutation.mutate({ id: editingSequence.id, data })
            setEditingSequence(null)
          }}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Click outside to close dropdown */}
      {activeDropdown && (
        <div 
          style={{ position: 'fixed', inset: 0, zIndex: 50 }}
          onClick={() => setActiveDropdown(null)}
        />
      )}

      {/* Modal d'activation */}
      <SequenceActivationModal
        sequence={sequenceToActivate}
        enrolledCount={sequenceToActivate ? (enrollmentCounts?.[sequenceToActivate.id] || 0) : 0}
        isOpen={showActivationModal}
        onClose={() => {
          setShowActivationModal(false)
          setSequenceToActivate(null)
        }}
        onConfirm={confirmActivation}
      />
    </div>
  )
}

// Modal pour créer une séquence
function CreateSequenceModal({ onClose, onCreate, isLoading }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Le nom est requis')
      return
    }
    onCreate({ name, description, status: 'draft' })
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgb(var(--rgb-shade) / 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        background: 'rgb(var(--rgb-surface-hover))',
        borderRadius: '12px',
        padding: '1.5rem',
        maxWidth: '500px',
        width: '90%',
        border: '1px solid rgb(var(--rgb-ink) / 0.1)'
      }} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginBottom: '1.5rem', fontSize: '1.25rem' }}>
          Nouvelle séquence
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              Nom de la séquence *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex: Suivi prospects LinkedIn"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Décrivez l'objectif de cette séquence..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.25rem',
                background: 'transparent',
                border: '1px solid rgb(var(--rgb-ink) / 0.2)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '0.75rem 1.25rem',
                background: 'rgb(var(--rgb-brand))',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              {isLoading ? 'Création...' : 'Créer la séquence'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Modal pour éditer une séquence
function EditSequenceModal({ sequence, onClose, onSave, isLoading }) {
  const [name, setName] = useState(sequence.name || '')
  const [description, setDescription] = useState(sequence.description || '')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Le nom est requis')
      return
    }
    onSave({ name, description })
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgb(var(--rgb-shade) / 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        background: 'rgb(var(--rgb-surface-hover))',
        borderRadius: '12px',
        padding: '1.5rem',
        maxWidth: '500px',
        width: '90%',
        border: '1px solid rgb(var(--rgb-ink) / 0.1)'
      }} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginBottom: '1.5rem', fontSize: '1.25rem' }}>
          Modifier la séquence
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              Nom de la séquence *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.25rem',
                background: 'transparent',
                border: '1px solid rgb(var(--rgb-ink) / 0.2)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '0.75rem 1.25rem',
                background: 'rgb(var(--rgb-brand))',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Panneau pour voir les inscrits à une séquence
function EnrollmentsPanel({ sequenceId, onClose }) {
  const [page, setPage] = useState(1)
  const limit = 50
  const { data, isLoading, error } = useQuery({
    queryKey: ['sequenceEnrollments', sequenceId, page],
    queryFn: () => getSequenceEnrollments(sequenceId, { page, limit }),
    enabled: !!sequenceId
  })

  // Handle both API Platform format (member) and custom format (enrollments)
  const enrollments = data?.items || data?.enrollments || data?.member || data?.['hydra:member'] || []
  const total = data?.total || data?.totalItems || data?.['hydra:totalItems'] || enrollments.length
  const hasMore = data?.hasMore ?? (page * limit < total)

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'rgb(var(--rgb-text-secondary) / 0.15)', color: 'var(--color-text-secondary)', label: 'En attente' },
      active: { bg: 'rgba(16,185,129,0.15)', color: '#10b981', label: 'Actif' },
      paused: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', label: 'Pausé' },
      completed: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', label: 'Terminé' },
      exited: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', label: 'Sorti' }
    }
    const s = styles[status] || styles.pending
    return (
      <span style={{
        padding: '0.2rem 0.5rem',
        background: s.bg,
        color: s.color,
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 500
      }}>
        {s.label}
      </span>
    )
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgb(var(--rgb-shade) / 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'rgb(var(--rgb-surface-hover))',
        borderRadius: '16px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Prospects inscrits ({total})
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
              Erreur: {error.message}
            </div>
          ) : enrollments.length === 0 ? (
            <div style={{
              textAlign: 'center', 
              padding: '3rem 2rem',
              background: 'rgb(var(--rgb-ink) / 0.02)',
              borderRadius: '12px'
            }}>
              <Users size={40} style={{ color: 'var(--color-text-muted)', marginBottom: '1rem' }} />
              <p style={{ color: 'var(--color-text-secondary)' }}>Aucun destinataire inscrit à cette séquence</p>
            </div>
          ) : (
            <div style={{
              background: 'rgb(var(--rgb-ink) / 0.02)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              {enrollments.map((enrollment, index) => {
                const prospect = enrollment.prospect
                const recipient = enrollment.recipient
                const contactEmail = prospect?.primaryEmail || prospect?.email || recipient?.email || ''
                const contactName = prospect
                  ? `${prospect?.firstName || ''} ${prospect?.lastName || ''}`.trim()
                  : (recipient?.fullName || `${recipient?.firstName || ''} ${recipient?.lastName || ''}`.trim())
                const displayName = contactName || contactEmail || '—'
                const initials = displayName
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0]?.toUpperCase())
                  .join('') || (contactEmail ? contactEmail[0]?.toUpperCase() : '?')

                return (
                  <div
                    key={enrollment.id}
                    style={{
                      padding: '0.875rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: index < enrollments.length - 1 ? '1px solid rgb(var(--rgb-ink) / 0.05)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'rgb(var(--rgb-ink) / 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-text-secondary)',
                        fontWeight: 600,
                        fontSize: '0.8rem'
                      }}>
                        {initials}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '0.9rem', marginBottom: '0.15rem' }}>
                          {displayName}
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>
                          {contactEmail && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <Mail size={11} />
                              {contactEmail}
                            </span>
                          )}
                          {(prospect?.companyName || prospect?.company) && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <Building size={11} />
                              {prospect?.companyName || prospect?.company}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                        <div>Étape {enrollment.currentStep}</div>
                        <div>{enrollment.emailsSent} email(s) envoyé(s)</div>
                      </div>
                      {getStatusBadge(enrollment.status)}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'rgb(var(--rgb-ink) / 0.1)',
                border: 'none',
                borderRadius: '8px',
                color: 'var(--color-text)',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              ◀
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'rgb(var(--rgb-ink) / 0.1)',
                border: 'none',
                borderRadius: '8px',
                color: 'var(--color-text)',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              ▶
            </button>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '0.6rem 1.25rem',
              background: 'rgb(var(--rgb-ink) / 0.1)',
              border: 'none',
              borderRadius: '8px',
              color: 'var(--color-text)',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  )
}

// Liste inline des prospects inscrits (pour l'onglet Prospects)
function ProspectsEnrollmentsList({ sequenceId }) {
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const limit = 25

  const { data, isLoading, error } = useQuery({
    queryKey: ['sequenceEnrollments', sequenceId, 'prospects', page, searchQuery, statusFilter],
    queryFn: () => getSequenceEnrollments(sequenceId, {
      page,
      limit,
      q: searchQuery || undefined,
      status: statusFilter || undefined,
      prospectOnly: true
    }),
    enabled: !!sequenceId
  })

  const enrollments = data?.items || data?.enrollments || data?.member || data?.['hydra:member'] || []
  const total = data?.total || data?.totalItems || data?.['hydra:totalItems'] || enrollments.length
  const hasMore = data?.hasMore ?? (page * limit < total)

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'rgb(var(--rgb-text-secondary) / 0.15)', color: 'var(--color-text-secondary)', label: 'En attente' },
      active: { bg: 'rgba(16,185,129,0.15)', color: '#10b981', label: 'Actif' },
      paused: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', label: 'Pause' },
      completed: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', label: 'Termine' },
      exited: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', label: 'Sorti' }
    }
    const s = styles[status] || styles.pending
    return (
      <span style={{
        padding: '0.2rem 0.5rem',
        background: s.bg,
        color: s.color,
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 500
      }}>
        {s.label}
      </span>
    )
  }

  return (
    <div>
      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ flex: 1, position: 'relative', maxWidth: '300px' }}>
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(1)
            }}
            style={{
              width: '100%',
              background: 'rgb(var(--rgb-ink) / 0.05)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '8px',
              padding: '0.5rem 0.75rem',
              color: 'var(--color-text)',
              fontSize: '0.9rem'
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          style={{
            background: 'rgb(var(--rgb-ink) / 0.05)',
            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
            borderRadius: '8px',
            padding: '0.5rem 0.75rem',
            color: 'var(--color-text)',
            fontSize: '0.9rem'
          }}
        >
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="active">Actif</option>
          <option value="completed">Termine</option>
        </select>
      </div>

      {/* Stats */}
      <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', marginBottom: '1rem' }}>
        {total} prospect(s) inscrit(s)
      </div>

      {/* Content */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
          Erreur: {error.message}
        </div>
      ) : enrollments.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgb(var(--rgb-ink) / 0.02)',
          borderRadius: '12px'
        }}>
          <Users size={40} style={{ color: 'var(--color-text-muted)', marginBottom: '1rem' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>
            {searchQuery || statusFilter
              ? 'Aucun prospect trouve avec ces criteres'
              : 'Aucun prospect inscrit a cette sequence'}
          </p>
        </div>
      ) : (
        <>
          <div style={{
            background: 'rgb(var(--rgb-ink) / 0.02)',
            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            {enrollments.map((enrollment, index) => {
              const prospect = enrollment.prospect
              const email = prospect?.primaryEmail || prospect?.email || ''
              const name = [prospect?.firstName, prospect?.lastName].filter(Boolean).join(' ') || email

              return (
                <div
                  key={enrollment.id}
                  style={{
                    padding: '0.875rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: index < enrollments.length - 1 ? '1px solid rgb(var(--rgb-ink) / 0.05)' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'rgba(59,130,246,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#3b82f6',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{name}</div>
                      <div style={{ display: 'flex', gap: '0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                        {email && <span>{email}</span>}
                        {prospect?.companyName && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Building size={11} />
                            {prospect.companyName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                      <div>Etape {enrollment.currentStep}</div>
                      <div>{enrollment.emailsSent} email(s)</div>
                    </div>
                    {getStatusBadge(enrollment.status)}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {(page > 1 || hasMore) && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  padding: '0.5rem 1rem',
                  background: page > 1 ? 'rgb(var(--rgb-ink) / 0.1)' : 'rgb(var(--rgb-ink) / 0.03)',
                  border: 'none',
                  borderRadius: '8px',
                  color: page > 1 ? 'var(--color-text)' : 'var(--color-text-muted)',
                  cursor: page > 1 ? 'pointer' : 'not-allowed',
                  fontSize: '0.85rem'
                }}
              >
                Precedent
              </button>
              <span style={{ padding: '0.5rem 1rem', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                Page {page}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!hasMore}
                style={{
                  padding: '0.5rem 1rem',
                  background: hasMore ? 'rgb(var(--rgb-ink) / 0.1)' : 'rgb(var(--rgb-ink) / 0.03)',
                  border: 'none',
                  borderRadius: '8px',
                  color: hasMore ? 'var(--color-text)' : 'var(--color-text-muted)',
                  cursor: hasMore ? 'pointer' : 'not-allowed',
                  fontSize: '0.85rem'
                }}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
